// @flow
import React from 'react';
import styled from 'styled-components';
import { usePagination, useSortBy, useTable, useTableState } from 'react-table';
import Button from 'components/common/Button';
import { fromNow } from 'lib/common';
import './EstimateTable.scss';

const Styles = styled.div`
  padding-top: 1rem;

  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    font-size: 100%;
    font: inherit;
    display: block;

    a {
      color: #339af0;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }

    th {
      font-weight: bold;
      border: 1px solid #ccc;
      padding: 6px 13px;
      white-space: nowrap;
    }

    td {
      border: 1px solid #ccc;
      padding: 8px 4px;
      text-align: center;
      /* The secret sauce */
      /* Each cell should grow equally */
      width: 1%;

      .wrapperBtns {
        display: flex;
        justify-content: center;
        button {
          width: 56px;
        }
      }
    }

    td:nth-child(1) {
      // min-width: 72px;
      font-size: 14px;
    }
    // td:nth-child(2) {
    //
    // }
    td:nth-child(3) {
      min-width: 116px;
      font-size: 14px;
    }
    // td:nth-child(4) {
    //   min-width: 64px;
    // }
    // td:nth-child(5) {
    //   min-width: 200px;
    // }

    tr {
      border-top: 1px solid #ccc;
      background-color: #fff;
    }

    tr:nth-child(2n) {
      background-color: #f8f8f8;
    }
  }

  .pagination {
    padding: 0.5rem;
  }
`;

const defaultColumn = {
  sort: 'numeric',
};

// Let's add a fetchData method to our Table component that will be used to fetch
// new data when pagination state changes
// We can also add a loading state to let our table know it's loading new data
function Table({
  columns,
  data,
  fetchData,
  loading,
  pageCount: controlledPageCount,
  onUploadEstimate,
  onRemoveEstimate,
  onUpdateEstimate,
  onViewEstimate,
  serverData,
  setData,
  totalCount,
}) {
  // Use useTableState to hoist the state (and state updater) out of the table and control it
  const tableState = useTableState({ pageIndex: 0 });

  // Now we can get our table state from the hoisted table state tuple
  const [{ pageIndex, pageSize }] = tableState;

  // Listen for changes in pagination and use the state to fetch our new data
  React.useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      debug: false,
      state: tableState, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: controlledPageCount,
    },
    useSortBy,
    usePagination,
  );

  if (!pageOptions.length) return null;

  // Render the UI for your table
  return (
    <div className="Table">
      <div className="show-menu">
        <div className="total-count">{`총 ${totalCount}건`}</div>
        <select
          className="select-option"
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map(newPageSize => (
            <option key={newPageSize} value={newPageSize}>
              {newPageSize}건씩 보기
            </option>
          ))}
        </select>
      </div>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                // Add the sorting props to control sorting. For this example
                // we can add them into the header props
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {/* Add a sort direction indicator */}
                  <span>{column.sorted ? (column.sortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {page.map((row, idx) => {
            return (
              prepareRow(row) || (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => {
                    if (cell.column.id === 'id') {
                      return <td {...cell.getCellProps()}>{cell.value.split('-')[0]}</td>;
                    }
                    if (cell.column.id === 'path') {
                      const date1 = cell.value.split('/');
                      return (
                        <td {...cell.getCellProps()}>
                          <a href={`https://images.cacheby.com/${cell.value}`}>
                            {date1[date1.length - 1]}
                          </a>
                        </td>
                      );
                    }
                    if (cell.column.id === 'type') {
                      return (
                        <td {...cell.getCellProps()}>
                          {cell.value === 'complete' ? '종료' : '요청중'}
                        </td>
                      );
                    }
                    if (cell.column.id === 'action') {
                      return (
                        <td {...cell.getCellProps()}>
                          <div className="wrapperBtns">
                            <Button theme="default" onClick={() => onViewEstimate(row.values.id)}>
                              보기
                            </Button>
                            <Button
                              theme="default"
                              color="red"
                              onClick={() => onRemoveEstimate(row.values.id)}
                            >
                              삭제
                            </Button>
                            {row.values.type !== 'complete' ? (
                              <Button
                                theme="outline"
                                onClick={() => onUpdateEstimate(row.values.id, idx)}
                              >
                                종료
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      );
                    }
                    if (cell.column.id === 'created_at') {
                      return <td {...cell.getCellProps()}>{fromNow(cell.value)}</td>;
                    }
                    return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                  })}
                </tr>
              )
            );
          })}
          {loading ? (
            // Use our custom loading state to show a loading indicator
            <tr>
              <td>Loading...</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {/*
        Pagination can be built however you'd like.
        This is just a very basic UI implementation:
      */}
      <div className="pagination">
        <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {'맨앞'}
        </Button>
        <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {'<'}
        </Button>
        <span className="page">
          <strong>
            {pageIndex + 1} / {pageOptions.length}
          </strong>
        </span>
        <Button onClick={() => nextPage()} disabled={!canNextPage}>
          {'>'}
        </Button>
        <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {'맨뒤'}
        </Button>
      </div>
    </div>
  );
}

type Props = {
  estimate: any,
  onUploadEstimate: any,
  onRemoveEstimate: any,
  onUpdateEstimate: any,
  onViewEstimate: any,
};

const EstimateTable = ({
  estimate,
  onUploadEstimate,
  onRemoveEstimate,
  onUpdateEstimate,
  onViewEstimate,
}: Props) => {
  const serverData = estimate.data;
  const columns = React.useMemo(
    () => [
      {
        Header: '견적 요청 리스트',
        columns: [
          {
            Header: '견적ID',
            accessor: 'id',
          },
          {
            Header: '견적 파일',
            accessor: 'path',
          },
          {
            Header: '요청 날짜',
            accessor: 'created_at',
          },
          {
            Header: '상태',
            accessor: 'type',
          },
          {
            Header: '액션',
            accessor: 'action',
          },
        ],
      },
    ],
    [],
  );

  // We'll start our table without any data
  const [data, setData] = React.useState(serverData);
  const [loading, setLoading] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(serverData.length);

  const fetchData = React.useCallback(
    ({ pageSize, pageIndex }) => {
      // This will get called when the table needs new data
      // You could fetch your data from literally anywhere,
      // even a server. But for this example, we'll just fake it.

      // Set the loading state
      setLoading(true);

      // We'll even set a delay to simulate a server here
      setTimeout(() => {
        const startRow = pageSize * pageIndex;
        const endRow = startRow + pageSize;
        // const sortCreatedAt = (a: any, b: any) => a.created_at - b.created_at;
        setData(serverData.slice(startRow, endRow));
        setTotalCount(serverData.length);

        // Your server could send back total page count.
        // For now we'll just fake it, too
        setPageCount(Math.ceil(serverData.length / pageSize));

        setLoading(false);
      }, 0);
    },
    [serverData],
  );

  return (
    <div className="EstimateTable">
      <Styles>
        <Table
          onUploadEstimate={onUploadEstimate}
          onRemoveEstimate={onRemoveEstimate}
          onUpdateEstimate={onUpdateEstimate}
          onViewEstimate={onViewEstimate}
          columns={columns}
          data={data}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          serverData={serverData}
          setData={setData}
          totalCount={totalCount}
        />
      </Styles>
    </div>
  );
};

export default EstimateTable;



// WEBPACK FOOTER //
// ./src/components/estimate/EstimateTable/EstimateTable.js